import type { ItemSpecs } from './types';

/**
 * Antemăsurătoarea (lista de cantități, devizul, necesarul pe hârtie) citită
 * automat și împărțită pe categorii, ca să devină cereri de ofertă. Nimic nu
 * pleacă singur: omul vede pozițiile, le corectează și face cererile.
 */

/** O poziție citită din document. */
export interface BoqLine {
  /** codul articolului din document (ex. „CA04B1”); '' dacă nu are */
  code: string;
  desc: string;
  /** null = documentul nu spune cantitatea */
  qty: number | null;
  unit: string;
  /** una din categoriile firmei, sau „Altele” */
  category: string;
  /** chirie: câte zile, dacă documentul spune; null = nu spune / cumpărare */
  rentDays: number | null;
  specs: ItemSpecs | null;
  /** de unde vine sau ce e de verificat; '' dacă nimic */
  note: string;
}

export interface BoqExtraction {
  /** numele lucrării, din antetul documentului; '' dacă nu reiese */
  title: string;
  lines: BoqLine[];
  /** ce trebuie să știe omul: pagini ilizibile, tabele tăiate */
  warnings: string[];
}

/** Categoria pozițiilor care nu se potrivesc cu nicio categorie a firmei. */
export const BOQ_OTHER = 'Altele';

/** Peste atât, documentul e prea mare pentru o singură citire. */
export const BOQ_MAX_LINES = 400;

/** Tipurile acceptate la încărcare, pentru `accept` din browser. */
export const BOQ_ACCEPT = '.pdf,.png,.jpg,.jpeg,.webp,.xlsx,.csv,.txt';

/** Cel mult atât de mare poate fi fișierul. */
export const BOQ_MAX_BYTES = 25 * 1024 * 1024;

/** Fără diacritice și majuscule: „ARMATURA” e aceeași categorie cu „Armătură”. */
const plainCat = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase();

/**
 * Categoriile în care se împarte antemăsurătoarea: cele folosite de firmă, apoi
 * cele propuse pentru meseria ei care lipsesc. Doar cele folosite ar trimite la
 * „Altele” tot ce n-a mai cerut firma până acum.
 */
export function boqCategories(used: string[], suggested: string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>([plainCat(BOQ_OTHER)]);
  for (const c of [...used, ...suggested]) {
    const k = plainCat(c);
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(c.trim());
  }
  return out;
}

/** Pozițiile, pe categorii, în ordinea în care apar prima dată în document. */
export function boqGroups(lines: BoqLine[]): { category: string; lines: BoqLine[] }[] {
  const out = new Map<string, BoqLine[]>();
  for (const l of lines) {
    const list = out.get(l.category) ?? [];
    list.push(l);
    out.set(l.category, list);
  }
  // „Altele” la urmă: acolo e ce trebuie împărțit de mână
  return [...out.entries()]
    .map(([category, ls]) => ({ category, lines: ls }))
    .sort((a, b) => Number(a.category === BOQ_OTHER) - Number(b.category === BOQ_OTHER));
}
